import React, { useCallback, useMemo } from 'react';
import _omit from 'lodash/omit';

import { DateFormat, formatDate } from '@lyearn/core/utils/date/formatDate';
import { formatRange, RangeFormat } from '@lyearn/core/utils/date/formatRange';
import { useTranslation } from '@lyearn/i18n';
import { ArrowIosDownward } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Stack, Typography } from '@lyearn/ui';
import { DateValue } from '@/modules/corporate/modules/tags/types';
import { DateTagType, FilterOperatorEnum } from '@/types/schema';

import { filterValueStyle } from '../styles';
import { DateValueTypeEnumSM, FilterValueProps, OnFilterChange } from '../types';
import {
  getDynamicDateValuePlaceholder,
  INITIAL_DATE_FILTER_STATE,
  isDateFilterEmpty,
  isDynamicDateFilterEmpty,
} from '../utils';
import DateConditionSelector from './DateConditionSelector';

function DateSelectFilter(props: FilterValueProps) {
  const { t } = useTranslation();
  const {
    filterValue,
    onFilterValueChange,
    disabled,
    error,
    tag,
    operator,
    disableDateValueTypeSelector,
  } = props;

  const dateTag = tag as DateTagType | undefined | null;

  const selectedFilterValues = filterValue ? JSON.parse(filterValue) : undefined;

  const dateTagValue = useMemo(
    () =>
      ({
        startTime: selectedFilterValues?.startTime,
        endTime: selectedFilterValues?.endTime,
      }) as DateValue,
    [selectedFilterValues?.endTime, selectedFilterValues?.startTime],
  );

  const dateValueType =
    selectedFilterValues?.dateValueType || INITIAL_DATE_FILTER_STATE.dateValueType;

  const offsetRelation =
    selectedFilterValues?.offsetRelation || INITIAL_DATE_FILTER_STATE.offsetRelation;

  const offsetDays = selectedFilterValues?.offsetDays || INITIAL_DATE_FILTER_STATE.offsetDays;

  const includeTime = Boolean(dateTag?.includeTime);
  const includeEndDate = operator === FilterOperatorEnum.Between;

  const onChange = useCallback(
    (val: OnFilterChange) => {
      const tagValue = { ...((val.tagValues || {}) as DateValue) };
      const restVal = _omit(val, 'tagValues');
      const dataToSave = {
        ...tagValue,
        ...restVal,
      };
      onFilterValueChange(JSON.stringify(dataToSave));
    },
    [onFilterValueChange],
  );

  const selectedValue = useMemo(() => {
    if (selectedFilterValues?.dateValueType === DateValueTypeEnumSM.Dynamic) {
      return getDynamicDateValuePlaceholder(
        t,
        selectedFilterValues?.offsetDays,
        selectedFilterValues?.offsetRelation,
      );
    }

    if (!dateTagValue?.startTime) {
      return '';
    }

    if (!includeEndDate || !dateTagValue?.endTime) {
      return formatDate(
        dateTagValue?.startTime,
        includeTime ? DateFormat.DateTime : DateFormat.Date,
        { includeYear: true },
      );
    }

    return formatRange(dateTagValue.startTime, dateTagValue.endTime, RangeFormat.Date, {
      includeYear: true,
    });
  }, [
    dateTagValue?.endTime,
    dateTagValue?.startTime,
    includeEndDate,
    includeTime,
    selectedFilterValues?.dateValueType,
    selectedFilterValues?.offsetDays,
    selectedFilterValues?.offsetRelation,
    t,
  ]);

  const hasError = useMemo(
    () =>
      error &&
      !disabled &&
      (selectedFilterValues?.dateValueType === DateValueTypeEnumSM.Dynamic
        ? isDynamicDateFilterEmpty(
            selectedFilterValues?.offsetDays,
            selectedFilterValues?.offsetRelation,
          )
        : isDateFilterEmpty(dateTagValue, includeEndDate)),
    [
      dateTagValue,
      disabled,
      error,
      includeEndDate,
      selectedFilterValues?.dateValueType,
      selectedFilterValues?.offsetDays,
      selectedFilterValues?.offsetRelation,
    ],
  );

  const Input = (
    <Stack
      alignItems="center"
      className={cn(
        hasError ? 'border-error-dark' : 'border-interactive-white-hover',
        'h-40 w-full rounded-8 border border-solid pe-12 ps-16',
      )}
      justifyContent="between">
      <Typography
        className="truncate"
        color={selectedValue ? 'text-text-primary' : 'text-text-placeholder'}
        title={selectedValue}
        variant="body-short-02">
        {selectedValue || t('Group:Editor.Value')}
      </Typography>
      <ArrowIosDownward
        className="h-20 w-20 flex-shrink-0 ms-12"
        fillColorToken="icon-placeholder"
      />
    </Stack>
  );

  return (
    <Stack
      className={cn(filterValueStyle, {
        'cursor-not-allowed': disabled,
      })}
      direction="col">
      {disabled ? (
        Input
      ) : (
        <DateConditionSelector
          dateTagValue={dateTagValue}
          dateValueType={dateValueType}
          disableValueTypeSelector={disableDateValueTypeSelector}
          includeEndDate={includeEndDate}
          includeTime={includeTime}
          offsetDays={offsetDays}
          offsetRelation={offsetRelation}
          operator={operator}
          onChange={onChange}>
          {Input}
        </DateConditionSelector>
      )}
      {hasError ? (
        <Typography className="mt-8" color="text-text-error" variant="body-short-03">
          {t('Common:Form.Error.ValueCantBeEmpty')}
        </Typography>
      ) : null}
    </Stack>
  );
}

export default DateSelectFilter;
